/**
 * Live sources for the Site 3D screen. Everything here works for any
 * coordinate on Earth without a server of ours:
 *
 *   imagery   Esri World Imagery (sharpest, 2025 capture here) or one month of
 *             Sentinel-2 L2A, mosaicked live by Microsoft Planetary Computer
 *   terrain   AWS Terrain Tiles (terrarium). At an audited site the survey cut
 *             is carved into those tiles (tools/build_site3d.py), served from
 *             /data/sites/<id>/dem behind sign-in, through the sitedem:// protocol
 *   buildings Overture Maps footprints (Google + Microsoft, traced from imagery).
 *             No lidar exists for rural India, so heights are estimated.
 *
 * Verified 4 Nov 2026 at 24.5614 N, 74.8165 E: every endpoint answers with
 * Access-Control-Allow-Origin: *, and the Planetary Computer register call
 * passes its CORS preflight.
 */
import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import { apiUrl } from '../api/client';

export const TERRAIN_URL = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';
export const ESRI_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
export const OVERTURE_BUILDINGS = 'https://tiles.overturemaps.org/2026-10-21.0/buildings.pmtiles';
const PC = 'https://planetarycomputer.microsoft.com/api';

export const ATTR = {
  esri: 'Imagery © Esri, Vantor, Earthstar Geographics',
  s2: 'Contains modified Copernicus Sentinel-2 data 2026, via Microsoft Planetary Computer',
  terrain: 'Terrain: AWS Terrain Tiles (SRTM, Copernicus)',
  buildings: 'Buildings: © Overture Maps Foundation (Google Open Buildings, Microsoft)',
};

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'] as const;
export const YEAR = 2026;

// ------------------------------------------------------------ protocols

let registered = false;
/** Carved tiles available per audited site and terrain set ("jul", "aug", "full"). */
const carved = new Map<string, Set<string>>();

export function registerSiteDem(siteId: string, sets: Record<string, string[]>): void {
  for (const [set, tiles] of Object.entries(sets)) carved.set(`${siteId}/${set}`, new Set(tiles));
}

/** Something cut into the terrain on the fly, such as the pond and reservoir basins. */
export interface TerrainCarver {
  covers(z: number, x: number, y: number): boolean;
  /** Lowers `heights` (m, 256 x 256, row by row) in place. */
  apply(z: number, x: number, y: number, heights: Float32Array): void;
}
let carver: TerrainCarver | null = null;
let carverVersion = 0;
/** Sets (or clears) the live carver; returns the version that demTiles() now puts in the url. */
export function setTerrainCarver(c: TerrainCarver | null): number {
  carver = c;
  return ++carverVersion;
}

async function carve(res: Response, c: TerrainCarver, z: number, x: number, y: number): Promise<ArrayBuffer> {
  const bmp = await createImageBitmap(await res.blob(), { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  const cv = new OffscreenCanvas(256, 256);
  const ctx = cv.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('no canvas');
  ctx.drawImage(bmp, 0, 0, 256, 256);
  const img = ctx.getImageData(0, 0, 256, 256);
  const d = img.data;
  const hts = new Float32Array(65536);
  for (let i = 0; i < 65536; i++) hts[i] = (d[i * 4] ?? 0) * 256 + (d[i * 4 + 1] ?? 0) + (d[i * 4 + 2] ?? 0) / 256 - 32768;
  c.apply(z, x, y, hts);
  for (let i = 0; i < 65536; i++) {
    const v = (hts[i] as number) + 32768, whole = Math.floor(v);
    d[i * 4] = Math.floor(whole / 256); d[i * 4 + 1] = whole % 256; d[i * 4 + 2] = Math.floor((v - whole) * 256); d[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return (await cv.convertToBlob({ type: 'image/png' })).arrayBuffer();
}

/** sitedem://<site>/<set>/v<carver>/{z}/{x}/{y}  - "base" or any tile not carved falls back to AWS. */
export function ensureProtocols(): void {
  if (registered) return;
  registered = true;
  const pm = new Protocol();
  maplibregl.addProtocol('pmtiles', pm.tile);
  maplibregl.addProtocol('sitedem', async (params, abort) => {
    const m = /^sitedem:\/\/([^/]+)\/([^/]+)\/v\d+\/(\d+)\/(\d+)\/(\d+)/.exec(params.url);
    if (!m) throw new Error(`bad terrain url ${params.url}`);
    const [, site = '', set = '', z = '', x = '', y = ''] = m;
    const key = `${z}/${x}/${y}`;
    const local = carved.get(`${site}/${set}`)?.has(key);
    const url = local
      ? apiUrl(`/data/sites/${site}/dem/${set}/${key}.png`)
      : TERRAIN_URL.replace('{z}', z).replace('{x}', x).replace('{y}', y);
    const res = await fetch(url, { signal: abort.signal });
    if (!res.ok) throw new Error(`terrain tile ${res.status}`);
    const c = carver;
    if (c && c.covers(+z, +x, +y)) return { data: await carve(res, c, +z, +x, +y) };
    return { data: await res.arrayBuffer() };
  });
}

export const demTiles = (site: string | null, set: string): string[] => [`sitedem://${site ?? 'none'}/${set}/v${carverVersion}/{z}/{x}/{y}`];

/**
 * The terrain source, with heights measured from the local ground (`base`, m).
 * Exaggeration multiplies height above zero; on raw sea-level heights x3 would
 * lift a 475 m plain by 950 m and leave the camera underground. Terrarium
 * unpacks as R*256 + G + B/256 - 32768, so moving the base is one shift.
 */
export function demSource(tiles: string[], base: number): maplibregl.RasterDEMSourceSpecification {
  return {
    type: 'raster-dem', tiles, tileSize: 256, maxzoom: 15, attribution: ATTR.terrain,
    encoding: 'custom', redFactor: 256, greenFactor: 1, blueFactor: 1 / 256, baseShift: 32768 + base,
  };
}

/** Ground height (m above sea level) at a point, from the public terrain tile at z12. */
export async function groundHeight(lat: number, lon: number): Promise<number> {
  const z = 12;
  const n = 2 ** z;
  const xf = ((lon + 180) / 360) * n;
  const yf = ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) * n;
  const x = Math.floor(xf), y = Math.floor(yf);
  const blob = await fetch(TERRAIN_URL.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y))).then((r) => r.blob());
  const bmp = await createImageBitmap(blob);
  const c = document.createElement('canvas');
  c.width = 256; c.height = 256;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return 0;
  ctx.drawImage(bmp, 0, 0);
  const d = ctx.getImageData(Math.min(255, Math.floor((xf - x) * 256)), Math.min(255, Math.floor((yf - y) * 256)), 1, 1).data;
  return (d[0] ?? 0) * 256 + (d[1] ?? 0) + (d[2] ?? 0) / 256 - 32768;
}

// ------------------------------------------------------------ Sentinel-2 by month

export interface MonthImagery {
  tiles: string;
  /** Clearest scene in the month, for the label. */
  sceneDate: string | null;
  cloud: number | null;
}

const monthCache = new Map<string, Promise<MonthImagery | null>>();

function monthRange(m: number): [string, string] {
  const start = new Date(Date.UTC(YEAR, m, 1));
  const end = new Date(Date.UTC(YEAR, m + 1, 0, 23, 59, 59));
  return [start.toISOString().replace('.000', ''), end.toISOString().replace('.000', '')];
}

/**
 * Registers a Planetary Computer mosaic of the month's Sentinel-2 scenes over
 * the point, clearest first, and returns its tile template. Monsoon months can
 * have no scene under the cloud limit; then this resolves to null.
 */
export function sentinelMonth(lat: number, lon: number, month: number): Promise<MonthImagery | null> {
  const key = `${lat.toFixed(2)},${lon.toFixed(2)},${month}`;
  const hit = monthCache.get(key);
  if (hit) return hit;
  const [t0, t1] = monthRange(month);
  const bbox = [lon - 0.06, lat - 0.06, lon + 0.06, lat + 0.06];
  const filter = {
    op: 'and',
    args: [
      { op: 'anyinteracts', args: [{ property: 'datetime' }, { interval: [t0, t1] }] },
      { op: '<=', args: [{ property: 'eo:cloud_cover' }, 40] },
    ],
  };
  const p = (async (): Promise<MonthImagery | null> => {
    const search = await fetch(`${PC}/stac/v1/search`, {
      method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ collections: ['sentinel-2-l2a'], bbox, 'filter-lang': 'cql2-json', filter, limit: 1, sortby: [{ field: 'eo:cloud_cover', direction: 'asc' }] }),
    }).then((r) => r.json()) as { features?: { properties: { datetime: string; 'eo:cloud_cover': number } }[] };
    const best = search.features?.[0];
    if (!best) return null;
    const reg = await fetch(`${PC}/data/v1/mosaic/register`, {
      method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ collections: ['sentinel-2-l2a'], bbox, 'filter-lang': 'cql2-json', filter, sortby: [{ field: 'eo:cloud_cover', direction: 'asc' }] }),
    }).then((r) => r.json()) as { searchid?: string };
    if (!reg.searchid) return null;
    return {
      tiles: `${PC}/data/v1/mosaic/${reg.searchid}/tiles/WebMercatorQuad/{z}/{x}/{y}@1x?collection=sentinel-2-l2a&assets=visual&asset_bidx=visual%7C1%2C2%2C3&nodata=0`,
      sceneDate: best.properties.datetime.slice(0, 10),
      cloud: Math.round(best.properties['eo:cloud_cover']),
    };
  })().catch(() => null);
  monthCache.set(key, p);
  return p;
}

// ------------------------------------------------------------ audited sites

export interface SiteMonth { m: string; s2: number; dswx: number; state: string; level: number | null; pit: number | null; water: boolean }
export interface SitePit { area_m2: number; mean_m: number; max_m: number; open_m3: number; lat: number; lon: number }
export interface Site {
  id: string; name: string; center: [number, number]; wall: [number, number][];
  depth_image: { url: string; coordinates: [number, number][] };
  pits: SitePit[]; dem_sets: Record<string, string[]>; months: SiteMonth[]; rl_to_msl: number;
  figures: { open_levelled_twice_m3: number; levelled_twice_m2: number; open_whole_m3: number; open_whole_range_m3: [number, number];
    refill_max_m3: [number, number]; refill_ceiling_m3: number; dug_total_m3: [number, number]; dug_area_m2: number };
  notes: Record<string, string>;
}
export interface SiteIndexEntry { id: string; name: string; center: [number, number] }

export async function loadSiteIndex(): Promise<SiteIndexEntry[]> {
  const r = await fetch(apiUrl('/data/sites/index.json'));
  if (!r.ok) return [];
  return ((await r.json()) as { sites: SiteIndexEntry[] }).sites;
}

export async function loadSite(id: string): Promise<Site | null> {
  const r = await fetch(apiUrl(`/data/sites/${id}/site.json`));
  return r.ok ? ((await r.json()) as Site) : null;
}

/** Distance in metres, good enough to decide whether a point is at a site. */
export function metresBetween(a: [number, number], b: [number, number]): number {
  const k = Math.PI / 180;
  const x = (b[0] - a[0]) * k * Math.cos(((a[1] + b[1]) / 2) * k);
  const y = (b[1] - a[1]) * k;
  return Math.hypot(x, y) * 6371000;
}

/** Which terrain set a month shows: the pits as they stood at the end of it. */
export function demSetFor(month: number | null): string {
  if (month === null) return 'full';
  if (month <= 5) return 'base';
  if (month === 6) return 'base';
  if (month === 7) return 'jul';
  if (month === 8) return 'aug';
  return 'full';
}
